import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getOrder as getOrderByToken } from '@app/api/action/Order';
import { RootReducer, Store } from '@app/app.reducers';
import {
	getFetchedOrder,
	resetCheckoutFlow,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { hasApprovalWorkflow } from '@app/product/utils/product-utils';
import { DataLayer } from '@app/shared/services/data-layer.service';
import { SeoService } from '@app/shared/services/seo.service';
import { UUID_REGEX } from '@app/shared/utils/util';
import { getUserPermissions } from '@app/user/reducers/user.reducer';
import { isFailure, isSuccess, pluckSuccessData } from '@granodigital/grano-remote-data';
import { firstValueFrom } from 'rxjs';
import { filter, map, shareReplay, tap } from 'rxjs/operators';

/** Order confirmed page */
@Component({
	selector: 'g-order-confirmed',
	templateUrl: './order-confirmed.component.html',
	styleUrls: ['./order-confirmed.component.scss'],
	standalone: false,
})
export class OrderConfirmedComponent implements OnInit {
	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly dataLayer = inject(DataLayer);
	private readonly seo = inject(SeoService);

	readonly order$ = this.store.select(getFetchedOrder).pipe(
		filter(isSuccess),
		pluckSuccessData,
		tap((order) => {
			this.store.dispatch(resetCheckoutFlow(undefined));
			this.dataLayer.addPurchase(order);
		}),
		shareReplay(1), // Do not repeat tap.
	);

	readonly showPrices$ = this.store
		.select(getUserPermissions)
		.pipe(map((permissions) => permissions?.show_prices ?? true));

	readonly needsApproval$ = this.order$.pipe(
		map((order) => order.products.some((item) => hasApprovalWorkflow(item.product!))),
	);

	/** Initialize order confirmed component */
	ngOnInit(): void {
		this.seo.setDirectly({ title: $localize`:@@SeoTitleOrderConfirmed:Order Confirmed` });

		/** Get access token either from URL params or query params */
		const accessToken =
			(this.route.snapshot.params.token as string) ||
			this.route.snapshot.queryParamMap.get('checkout-stamp');

		if (typeof accessToken === 'string' && UUID_REGEX.test(accessToken)) {
			this.store.dispatch(getOrderByToken(accessToken));
		} else {
			void this.router.navigate(['/']);
		}

		// Navigate to home if order has errored or if it's initialized without data
		void firstValueFrom(
			this.store
				.select(getFetchedOrder)
				.pipe(filter((order) => isFailure(order) || (isSuccess(order) && !order.data))),
		).then(() => this.router.navigate(['/']));
	}
}
